import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

/*
 * ActiveSessionContext
 * --------------------
 * Holds the CURRENT, UNSAVED conversation in memory only, for the duration
 * of a single authenticated tab session. This exists purely so that
 * navigating between authenticated routes (Chat <-> Support <-> Safety etc.)
 * does not reset the live conversation.
 *
 * Privacy guarantees:
 *   - State lives in React memory ONLY. Nothing here is written to
 *     IndexedDB, localStorage, sessionStorage, cookies, or the backend.
 *   - Cleared automatically on logout (via AuthContext isAuthenticated).
 *   - Cleared when the tab/process goes away (React state is discarded).
 *   - No logging or analytics of conversation content.
 *
 * User-controlled save/delete behavior is UNCHANGED:
 *   - "Save to Diary" still explicitly writes to IndexedDB via Chat.js.
 *   - Nothing is auto-persisted on unmount, navigation, or any other event.
 */

const ActiveSessionContext = createContext(null);

export const useActiveSession = () => {
  const ctx = useContext(ActiveSessionContext);
  if (!ctx) {
    throw new Error('useActiveSession must be used within ActiveSessionProvider');
  }
  return ctx;
};

const createFreshSessionIdentity = () => ({
  sessionId: `session_${Date.now()}`,
  sessionStartTime: new Date().toISOString(),
});

export const ActiveSessionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [messages, setMessages] = useState([]);
  const [sessionSaved, setSessionSaved] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [identity, setIdentity] = useState(createFreshSessionIdentity);

  // A live ref to the current sessionId. Async callers (e.g. in-flight
  // chat requests whose component may unmount before resolution) can use
  // this to detect that the session has been discarded / user logged out
  // and skip repopulating cleared state.
  const sessionIdRef = useRef(identity.sessionId);
  useEffect(() => {
    sessionIdRef.current = identity.sessionId;
  }, [identity.sessionId]);

  // Explicitly discard the current in-memory conversation and start fresh.
  // Does NOT touch any persistent storage.
  const discardSession = useCallback(() => {
    setMessages([]);
    setSessionSaved(false);
    setInput('');
    setIsLoading(false);
    setIdentity(createFreshSessionIdentity());
  }, []);

  // Clear in-memory session the moment the user logs out / locks.
  // We only clear on the authenticated -> unauthenticated transition so
  // that re-renders of the provider never wipe an active conversation.
  const wasAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      // Transition: logged in -> logged out. Wipe transient state.
      setMessages([]);
      setSessionSaved(false);
      setInput('');
      setIsLoading(false);
      setIdentity(createFreshSessionIdentity());
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const value = {
    messages,
    setMessages,
    sessionSaved,
    setSessionSaved,
    input,
    setInput,
    isLoading,
    setIsLoading,
    sessionId: identity.sessionId,
    sessionStartTime: identity.sessionStartTime,
    sessionIdRef,
    discardSession,
  };

  return (
    <ActiveSessionContext.Provider value={value}>
      {children}
    </ActiveSessionContext.Provider>
  );
};
